import useI18n from '@/hooks/useI18n';

// Placeholders shaped like the brand dashboard panels, so each panel can render
// the moment its own request lands instead of the page waiting on the slowest.
// The shimmer lives in index.css (.skeleton-bone).

const Bone = ({ className = '' }: { className?: string }) => (
  <div aria-hidden className={`skeleton-bone rounded-md ${className}`} />
);

/**
 * One line of text: the box takes the real line height, so nothing shifts
 * when the text replaces it, and the bone sits inside it like a cap height.
 */
const Line = ({ box, bone }: { box: string; bone: string }) => (
  <div aria-hidden className={`flex items-center ${box}`}>
    <Bone className={bone} />
  </div>
);

const card = 'rounded-3xl border border-[#E2E8F0] bg-white';

export const SummaryRowSkeleton = () => (
  <div
    data-testid="summary-skeleton"
    className="flex w-full flex-col items-start justify-between gap-4 pb-4 xl:flex-row"
  >
    <div className="flex flex-col items-start gap-6 md:flex-row md:items-end md:gap-8">
      <div className="flex flex-col gap-2">
        <Line box="h-[23px]" bone="h-4 w-40" />
        <Line box="h-[22px]" bone="h-3 w-56" />
      </div>
      <div className="flex flex-wrap items-center gap-6 md:gap-8">
        {['w-20', 'w-40', 'w-14'].map((w) => (
          <div key={w} className="flex flex-col gap-2">
            <Line box="h-4" bone="h-3 w-16" />
            <Line box="h-5" bone={`h-4 ${w}`} />
          </div>
        ))}
      </div>
    </div>
    <Bone className="size-[42px] shrink-0 self-end rounded-[18px] xl:self-start" />
  </div>
);

const KpiCardSkeleton = () => (
  <div className={`flex min-w-0 flex-1 flex-col gap-6 p-5 ${card}`}>
    <div className="flex flex-col gap-2">
      <Line box="h-5" bone="h-3.5 w-28" />
      <Line box="h-10" bone="h-7 w-32" />
      <Line box="h-4" bone="h-3 w-44" />
    </div>
    <div className="flex flex-col gap-3">
      {[0, 1].map((i) => (
        <div key={i} className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <Line box="h-5" bone="h-3 w-24" />
            <Line box="h-5" bone="h-3 w-12" />
          </div>
          <Bone className={`h-2 rounded-[5px] ${i ? 'w-1/2' : 'w-4/5'}`} />
        </div>
      ))}
    </div>
  </div>
);

export const KpiCardsSkeleton = () => (
  <div
    data-testid="kpi-skeleton"
    className="flex w-full flex-col items-stretch gap-4 lg:flex-row lg:items-start"
  >
    <KpiCardSkeleton />
    <KpiCardSkeleton />
    <KpiCardSkeleton />
  </div>
);

export const StatsGridSkeleton = () => (
  <div
    data-testid="stats-skeleton"
    className="flex w-full flex-wrap overflow-hidden rounded-3xl border border-[#E9E9E9] bg-white md:flex-nowrap"
  >
    {[0, 1, 2, 3].map((i) => (
      <div
        key={i}
        className={`flex flex-[1_0_50%] flex-col gap-1 p-4 md:flex-1 ${
          i < 3 ? 'md:border-r md:border-[#E9E9E9]' : ''
        }`}
      >
        <Line box="h-8" bone="h-6 w-16" />
        <Line box="h-4" bone="h-3 w-20" />
      </div>
    ))}
  </div>
);

export const TopPostsSkeleton = () => (
  <div
    data-testid="top-posts-skeleton"
    className={`flex w-full flex-col gap-6 px-6 py-8 ${card} border-[#E5E7EB]`}
  >
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col">
        <Line box="h-6" bone="h-4 w-28" />
        <Line box="h-4" bone="h-3 w-52" />
      </div>
      <Bone className="h-11 w-36 rounded-full max-sm:w-full" />
    </div>
    <div className="flex w-full flex-wrap items-start gap-5 md:flex-nowrap">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          // Match TopPostsPanel: the sixth card only fits on 2xl screens.
          className={`min-w-0 flex-1 flex-col gap-3 ${i >= 5 ? 'hidden 2xl:flex' : 'flex'}`}
        >
          <Bone className="aspect-[190/269] w-full max-w-[190px] rounded-xl" />
          <div className="flex flex-col gap-0.5">
            <Line box="h-5" bone="h-3.5 w-3/4" />
            <Line box="h-4" bone="h-3 w-1/2" />
          </div>
          <Line box="h-4" bone="h-3 w-2/3" />
        </div>
      ))}
    </div>
  </div>
);

export const CreatorsReachSkeleton = () => (
  <div
    data-testid="reach-skeleton"
    className={`flex w-full flex-col lg:flex-row ${card} border-[#EAEAEA]`}
  >
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        className={`flex min-w-0 flex-1 flex-col gap-4 p-6 ${
          i < 2 ? 'border-[#EAEAEA] lg:border-r' : ''
        }`}
      >
        <Line box="h-4" bone="h-3 w-20" />
        {['w-full', 'w-4/5', 'w-3/5', 'w-2/5'].map((w) => (
          <div key={w} className="flex items-center gap-3">
            <Bone className="h-3 w-10" />
            <Bone className={`h-2 rounded-[5px] ${w}`} />
          </div>
        ))}
      </div>
    ))}
  </div>
);

/** The whole campaign section, shown before we know which campaign to load. */
export const CampaignSectionSkeleton = () => {
  const { t } = useI18n();

  return (
    <div
      data-testid="campaign-section-skeleton"
      role="status"
      aria-busy="true"
      className="flex flex-col items-center px-6"
    >
      <span className="sr-only">{t('common.loading')}</span>
      <div className="flex w-full flex-col gap-4 pb-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col">
          <Line box="h-6" bone="h-4 w-24" />
          <Line box="h-6" bone="h-3.5 w-72 max-w-full" />
        </div>
        <Bone className="h-11 w-48 rounded-full max-lg:w-full" />
      </div>
      <div className="flex w-full flex-col gap-4 rounded-3xl border border-[#E9E9E9] bg-[#F9F9F9] p-5 lg:p-6">
        <SummaryRowSkeleton />
        <KpiCardsSkeleton />
        <StatsGridSkeleton />
      </div>
    </div>
  );
};
